import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { TrendingUp, Menu, X, Globe, Smartphone, ExternalLink } from "lucide-react";
import { APP_NAME } from "@/constants/app";
import { api } from "@/lib/api";
import ContactModal from "@/components/ContactModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function Header() {
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [investModalOpen, setInvestModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-white border-b border-sif card-shadow"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="w-9 h-9 rounded-lg bg-sif-primary flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-lg font-semibold text-sif-primary leading-tight">{APP_NAME}</div>
            <div className="text-[10px] text-sif-muted uppercase tracking-widest">India SIF Intelligence</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          <Link
            data-testid="nav-home"
            to="/"
            className={`transition-colors ${pathname === "/" ? "text-sif-primary font-semibold" : "text-sif-muted hover:text-sif-primary"}`}
          >
            Funds
          </Link>
          <Link
            data-testid="nav-compare"
            to="/compare"
            className={`transition-colors ${pathname.startsWith("/compare") ? "text-sif-primary font-semibold" : "text-sif-muted hover:text-sif-primary"}`}
          >
            Compare
          </Link>
          <Link
            data-testid="nav-amcs"
            to="/amcs"
            className={`transition-colors ${pathname.startsWith("/amcs") ? "text-sif-primary font-semibold" : "text-sif-muted hover:text-sif-primary"}`}
          >
            AMCs
          </Link>
          <Link
            data-testid="nav-blogs"
            to="/blogs"
            className={`transition-colors ${pathname.startsWith("/blogs") ? "text-sif-primary font-semibold" : "text-sif-muted hover:text-sif-primary"}`}
          >
            Blogs
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="header-cta"
            onClick={() => setInvestModalOpen(true)}
            className="hidden md:inline-flex items-center px-4 py-2 rounded-lg bg-sif-primary text-white text-sm font-semibold hover:opacity-90 transition"
          >
            Start Investing
          </button>

          <button
            data-testid="contact-cta"
            onClick={() => setContactModalOpen(true)}
            className="hidden md:inline-flex items-center px-4 py-2 rounded-lg border border-sif-primary text-sif-primary bg-white text-sm font-semibold hover:bg-slate-50 transition"
          >
            Contact Now
          </button>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="md:hidden p-2 rounded-lg text-sif-muted hover:text-sif-primary hover:bg-slate-100 transition"
            aria-label="Toggle Navigation Menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileMenuOpen && (
        <div data-testid="mobile-nav-drawer" className="md:hidden border-t border-sif bg-white px-4 py-4 space-y-3 shadow-lg">
          <nav className="flex flex-col space-y-3 font-medium text-base">
            <Link
              data-testid="mobile-nav-home"
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-3 py-2 rounded-lg transition-colors ${pathname === "/" ? "bg-slate-100 text-sif-primary font-semibold" : "text-sif-muted hover:bg-slate-50 hover:text-sif-primary"}`}
            >
              Funds
            </Link>
            <a
              href="#"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg text-sif-muted hover:bg-slate-50 hover:text-sif-primary transition-colors"
            >
              Analytics
            </a>
            <Link
              data-testid="mobile-nav-compare"
              to="/compare"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-3 py-2 rounded-lg transition-colors ${pathname.startsWith("/compare") ? "bg-slate-100 text-sif-primary font-semibold" : "text-sif-muted hover:bg-slate-50 hover:text-sif-primary"}`}
            >
              Compare
            </Link>
            <Link
              data-testid="mobile-nav-amcs"
              to="/amcs"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-3 py-2 rounded-lg transition-colors ${pathname.startsWith("/amcs") ? "bg-slate-100 text-sif-primary font-semibold" : "text-sif-muted hover:bg-slate-50 hover:text-sif-primary"}`}
            >
              AMCs
            </Link>
            <Link
              data-testid="mobile-nav-blogs"
              to="/blogs"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-3 py-2 rounded-lg transition-colors ${pathname.startsWith("/blogs") ? "bg-slate-100 text-sif-primary font-semibold" : "text-sif-muted hover:bg-slate-50 hover:text-sif-primary"}`}
            >
              Blogs
            </Link>
          </nav>

          <div className="pt-2 border-t border-sif flex flex-col gap-2">
            <button
              data-testid="mobile-header-cta"
              onClick={() => {
                setMobileMenuOpen(false);
                setInvestModalOpen(true);
              }}
              className="w-full py-2.5 rounded-lg bg-sif-primary text-white text-sm font-semibold text-center hover:opacity-90 transition"
            >
              Start Investing
            </button>
            <button
              data-testid="mobile-contact-cta"
              onClick={() => {
                setMobileMenuOpen(false);
                setContactModalOpen(true);
              }}
              className="w-full py-2.5 rounded-lg border border-sif-primary text-sif-primary bg-white text-sm font-semibold text-center hover:bg-slate-50 transition"
            >
              Contact Now
            </button>
          </div>
        </div>
      )}

      {/* Start Investing Dialog Modal */}
      <Dialog open={investModalOpen} onOpenChange={setInvestModalOpen}>
        <DialogContent className="sm:max-w-md border-slate-100 rounded-2xl bg-white p-6 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 text-center">
              Start Your Investment
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 text-center mt-1">
              Select how you would like to open FundzBazar to begin investing.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4 mt-2">
            <a
              href="https://fundzbazar.com/Link/8sIHN6UjxcA"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setInvestModalOpen(false)}
              className="flex flex-col items-center p-5 border border-slate-100 rounded-2xl bg-slate-50/50 hover:bg-white hover:border-sif-primary hover:shadow-lg transition-all duration-200 group text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3 transition-transform duration-200 group-hover:scale-110">
                <Globe className="w-6 h-6" />
              </div>
              <span className="text-sm font-bold text-slate-900">Web Browser</span>
              <span className="text-[11px] text-slate-500 mt-1 leading-normal">Invest on your desktop or laptop</span>
              <div className="mt-4 text-xs font-semibold text-sif-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Open Website <ExternalLink className="w-3 h-3" />
              </div>
            </a>

            <a
              href="https://fundzbazar.com/Link/WQizC9hQ6DM"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setInvestModalOpen(false)}
              className="flex flex-col items-center p-5 border border-slate-100 rounded-2xl bg-slate-50/50 hover:bg-white hover:border-sif-primary hover:shadow-lg transition-all duration-200 group text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-3 transition-transform duration-200 group-hover:scale-110">
                <Smartphone className="w-6 h-6" />
              </div>
              <span className="text-sm font-bold text-slate-900">Mobile Link</span>
              <span className="text-[11px] text-slate-500 mt-1 leading-normal">Invest on your mobile device</span>
              <div className="mt-4 text-xs font-semibold text-sif-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Open Mobile Link <ExternalLink className="w-3 h-3" />
              </div>
            </a>
          </div>
          <div className="text-[10px] text-slate-400 text-center mt-2 border-t border-slate-100 pt-3.5 font-medium">
            You will be securely redirected to FundzBazar to continue.
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Now Dialog Modal */}
      <ContactModal open={contactModalOpen} onOpenChange={setContactModalOpen} />
    </header>
  );
}
